import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import EmailShare from './EmailShare';
import FacebookShare from './FacebookShare';
import SMSShare from './SMSShare';
import TwitterShare from './TwitterShare';
import WhatsAppShare from './WhatsAppShare';

import './SocialButtonsList.scss';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import generateHash from '../../utils/generateHash';

type ShareButtonType = 'default' | 'twitter' | 'whatsapp' | 'sms' | 'email';

type ShareButtonMessage = { [key in ShareButtonType]?: string };

export type SocialButtonsListProps = {
  messages: ShareButtonMessage;
  subject: string;
};

const SocialButtonsList: FunctionComponent<SocialButtonsListProps> = ({
  messages,
  subject,
}) => {
  const trackingData = { sub_section: 'story' };
  const { pageType, pageShortName, currentUrl } = useNotNullableContext(
    PageMetaContext,
  );
  const { t } = useTranslation('common', { useSuspense: false });
  const defaultMessage = messages.default
    ? messages.default
    : t('common.shareMessageFallback');

  return (
    <ul className="jg-pages-social-button-list">
      <li>
        <FacebookShare
          url={`${currentUrl}?utm_source=facebook&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-share&utm_term=${generateHash()}`}
          className="jg-pages-social-button-list__button"
          trackingData={trackingData}
        />
      </li>
      <li>
        <TwitterShare
          url={encodeURIComponent(
            `${currentUrl}?utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`,
          )}
          message={encodeURIComponent(messages.twitter || defaultMessage)}
          className="jg-pages-social-button-list__button"
          trackingData={trackingData}
        >
          <span className="jg-sr-only">{t('common.shareOn')}</span>
          Twitter
        </TwitterShare>
      </li>
      <li>
        <EmailShare
          subject={subject}
          message={
            messages.email ||
            `${defaultMessage} ${currentUrl}?utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-email&utm_term=${generateHash()}`
          }
          className="jg-pages-social-button-list__button"
          trackingData={trackingData}
        >
          <span className="jg-sr-only">{t('common.shareBy')}</span>
          {t('common.email')}
        </EmailShare>
      </li>
      <li className="jg-display-n@lg">
        <SMSShare
          url={`${currentUrl}?utm_source=sms&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-sms&utm_term=${generateHash()}`}
          className="jg-pages-social-button-list__button"
          message={messages.sms || defaultMessage}
          trackingData={trackingData}
        >
          <span className="jg-sr-only">{t('common.shareBy')}</span>
          {t('common.sms')}
        </SMSShare>
      </li>
      <li className="jg-display-n@lg">
        <WhatsAppShare
          message={messages.whatsapp || defaultMessage}
          url={`${currentUrl}?utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`}
          className="jg-pages-social-button-list__button"
        >
          <span className="jg-sr-only">Share by </span>
          WhatsApp
        </WhatsAppShare>
      </li>
    </ul>
  );
};

export default SocialButtonsList;
